import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * Expenses — ROADMAP.md §10.1, business-rules.md §6 (Money), data-model.md §6.
 *
 * **Every amount that crosses this boundary is an integer number of cents.**
 * The wire format is `amountCents: 1250`, not `12.50` and not `"$12.50"`.
 * `parseMoneyInput` in `@shared/money` is what turns what somebody typed into
 * that number, and `formatCents` is what turns it back — at the display edge
 * and nowhere else (CLAUDE.md §4).
 *
 * **Nothing here is a raw enum on a screen.** `category` and `status` both
 * carry a label map, and the views below send the label alongside the value.
 * `deep_cleaning` reaching a user is defect P5 and this is the shape that stops
 * its equivalent happening here.
 */

// ---------------------------------------------------------------------------
// The two vocabularies
// ---------------------------------------------------------------------------

export const EXPENSE_CATEGORY_VALUES = [
  'supplies',
  'fuel',
  'equipment',
  'parking',
  'other',
] as const;

export type ExpenseCategoryValue = (typeof EXPENSE_CATEGORY_VALUES)[number];

/** Display names. A raw `supplies` must never reach a screen (P5). */
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategoryValue, string> = {
  supplies: 'Supplies',
  fuel: 'Fuel',
  equipment: 'Equipment',
  parking: 'Parking',
  other: 'Other',
};

export const EXPENSE_STATUS_VALUES = ['pending', 'approved', 'declined'] as const;
export type ExpenseStatusValue = (typeof EXPENSE_STATUS_VALUES)[number];

/**
 * **"Declined", never "rejected"** — the prototype's word, and P11 is the
 * defect where the two vocabularies disagreed on one screen. `job_request` and
 * `job_change_request` both already say declined; this is the third.
 */
export const EXPENSE_STATUS_LABELS: Record<ExpenseStatusValue, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
};

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/**
 * Cents, as a whole number.
 *
 * The cap is $100,000 in cents. Not arbitrary: `amount_cents` is a MySQL `int`,
 * which stops at 2,147,483,647, and a value near that limit would be accepted
 * here and then silently clamp on write. A ceiling far below the column's is the
 * one that fails in words rather than in arithmetic.
 */
const amountCentsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(0, 'An amount cannot be negative')
  .max(10_000_000, 'That is over $100,000 — check the amount');

const descriptionSchema = z
  .string()
  .trim()
  .max(2000, 'Must be 2000 characters or fewer')
  .transform((value) => (value.length === 0 ? null : value))
  .nullable()
  .optional();

/**
 * What a cleaner or an admin submits.
 *
 * `employeeId` and `jobId` are both optional and both mean something when
 * absent: no person behind the cost, and no single job it belongs to. The
 * employee endpoint ignores `employeeId` entirely and fills it from the session
 * — a cleaner filing an expense against somebody else is a hole, and the way to
 * not have it is to never read the field on that route.
 */
export const createExpenseSchema = z.object({
  category: z.enum(EXPENSE_CATEGORY_VALUES),
  amountCents: amountCentsSchema,
  incurredOn: calendarDateSchema,
  description: descriptionSchema,
  jobId: z.uuid().nullable().optional(),
  employeeId: z.uuid().nullable().optional(),
});

export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

/**
 * Editing one.
 *
 * **Only a `pending` expense can be edited**, enforced in the service. Changing
 * the amount on a row somebody already approved would rewrite a decision that
 * has been made, and the approval carries a date and a name.
 */
export const updateExpenseSchema = createExpenseSchema;
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const approveExpenseSchema = z.object({
  reviewNote: descriptionSchema,
});
export type ApproveExpenseInput = z.infer<typeof approveExpenseSchema>;

/** Declining. **The note is required and the submitter reads it** — same as P16. */
export const declineExpenseSchema = z.object({
  reviewNote: z
    .string()
    .trim()
    .min(
      1,
      'Say why — the person who submitted this sees it, and a bare "declined" is a phone call',
    )
    .max(2000, 'Must be 2000 characters or fewer'),
});
export type DeclineExpenseInput = z.infer<typeof declineExpenseSchema>;

// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------

export const expenseListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...EXPENSE_STATUS_VALUES, 'all']).default('all'),
  category: z.enum([...EXPENSE_CATEGORY_VALUES, 'all']).default('all'),
  employeeId: z.uuid().optional(),
  /** Inclusive both ends, on `incurred_on`. */
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});

export type ExpenseListQuery = z.infer<typeof expenseListQuerySchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

/**
 * What a cleaner sees of their own expense.
 *
 * **No reviewer name.** Who approved it is the office's business; the answer and
 * the reason are the submitter's. Widening this is what `payload-leak-sweep`
 * would catch, and it is deliberately narrow rather than accidentally so.
 */
export interface ExpenseView {
  id: string;

  category: ExpenseCategoryValue;
  categoryLabel: string;

  amountCents: number;

  incurredOn: string;
  description: string | null;

  status: ExpenseStatusValue;
  statusLabel: string;

  /** The office's answer. Shown on approve and decline both (P16). */
  reviewNote: string | null;
  reviewedAt: string | null;

  /** True when there is a receipt to fetch. **The key itself never leaves the server.** */
  hasReceipt: boolean;
  receiptFilename: string | null;

  jobId: string | null;
  jobNumber: string | null;

  submittedAt: string;

  /** Only a pending row can be edited or withdrawn — the service enforces it. */
  isEditable: boolean;
}

/** The office's view: the same, plus who spent it and who decided. */
export interface AdminExpenseView extends ExpenseView {
  employeeId: string | null;
  employeeName: string | null;
  submittedByName: string;
  reviewedByName: string | null;
}

/**
 * The totals above the admin list.
 *
 * **All three are cents and all three are always a number** — `0` is `0`, never
 * a dash (P1, P10). An empty queue is a real answer and it reads as one.
 */
export interface ExpenseTotals {
  pendingCents: number;
  approvedCents: number;
  declinedCents: number;
  /** How many rows are waiting, which is the number the office acts on. */
  pendingCount: number;
}
